import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { usePlaybackSession } from "@/domains/certifications/hooks/usePlaybackSession";
import { getVideoDetails, toBackendUrl } from "@/domains/certifications/utils";
import { VideoPlayerModalProps } from "@/features/certifications/module/components/types";

import "./styles.css";

const MODAL_WIDTH = 960;
const CHECK_ICON_SIZE = 18;

const VideoPlayerModal = ({
  certificationSlug,
  selection,
  onClose,
}: VideoPlayerModalProps) => {
  const { t } = useTranslation();
  const shownRef = useRef(selection);
  if (selection) {
    shownRef.current = selection;
  }
  const shown = shownRef.current;
  const thumbnailUrl = shown
    ? getVideoDetails(shown.video)?.thumbnailUrl
    : null;
  const poster = thumbnailUrl ? toBackendUrl(thumbnailUrl) : undefined;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const {
    videoRef,
    playbackUrl,
    progress,
    isStarting,
    isError,
    isTrackingStopped,
  } = usePlaybackSession({
    certificationSlug,
    contentItemId: selection?.video.id ?? null,
  });

  let status = null;
  if (isTrackingStopped) {
    status = (
      <p className="module-warning-text body-s-regular">
        {t("certification.module.player.trackingStopped")}
      </p>
    );
  } else if (progress?.isComplete) {
    status = (
      <p className="module-player-watched flex items-center gap-200 body-s-medium">
        <Check size={CHECK_ICON_SIZE} className="flex-none" />

        {t("certification.module.player.watched")}
      </p>
    );
  } else if (progress) {
    status = (
      <p className="certification-muted body-s-regular">
        {t("certification.module.player.coveredPercent", {
          percent: Math.floor(progress.coveredPercent),
        })}
      </p>
    );
  }

  return (
    <BaseModal
      open={selection !== null}
      width={MODAL_WIDTH}
      onCancel={onClose}
      destroyOnClose
      title={
        shown
          ? t("certification.module.videos.title", {
              number: shown.number,
              title: shown.video.title,
            })
          : undefined
      }
    >
      <div className="flex flex-col gap-300">
        <div className="module-player flex items-center justify-center">
          {isStarting && <BaseSpinner color="white" />}

          {(isError || (playbackUrl !== null && failedUrl === playbackUrl)) && (
            <p className="module-player-error body-s-regular" role="alert">
              {t("certification.module.player.error")}
            </p>
          )}

          {playbackUrl && failedUrl !== playbackUrl && (
            <video
              ref={videoRef}
              className="full-width full-height"
              src={playbackUrl}
              poster={poster}
              aria-label={shown?.video.title}
              controls
              playsInline
              controlsList="nodownload"
              onError={() => setFailedUrl(playbackUrl)}
            />
          )}
        </div>

        {status}
      </div>
    </BaseModal>
  );
};

export default VideoPlayerModal;
